import React, { useState, useEffect } from 'react';

import EMAILTEXT from '../assets/images/epost.jpg';
import {
    getConsentPrefs,
    OPEN_CONSENT_EVENT,
    CONSENT_CHANGED_EVENT,
} from '../utils/cookieConsent';

const API_URL = 'https://freelancer-rest-api.azurewebsites.net/api/ReceivedMessageFromContactForm';

function ContactMe() {
    const [recaptchaAllowed, setRecaptchaAllowed] = useState(
        () => getConsentPrefs()?.recaptcha === true
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const handler = () => setRecaptchaAllowed(getConsentPrefs()?.recaptcha === true);
        window.addEventListener(CONSENT_CHANGED_EVENT, handler);
        return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isSubmitting) return;

        const form = e.currentTarget;
        const formData = new FormData(form);

        const payload = {
            FullName: (formData.get('FullName') as string)?.trim() ?? '',
            EMail: (formData.get('email') as string)?.trim() ?? '',
            Phone: (formData.get('phone') as string)?.trim() ?? '',
            Message: (formData.get('message') as string)?.trim() ?? '',
            Token: ''
        };

        setIsSubmitting(true);
        setErrorMessage(null);
        setIsSuccess(false);

        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || `Server error (${response.status})`);
            }

            setIsSuccess(true);
            form.reset();
        } catch (err) {
            setErrorMessage(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page">
            <div className="page-title">
                <div className="container">
                    <h1>Contact</h1>
                </div>
            </div>
            <section className="section contact">
                <div className="container" data-aos="fade-up" data-aos-delay="100">

                    <div className="mb-4" data-aos="fade-up" data-aos-delay="200">
                        <iframe
                            style={{ border: 0, width: '100%', height: '270px' }}
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13404.621873955253!2d8.744686820573945!3d50.192921141155615!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bd05786f530f97%3A0x1c224353694bdd70!2s61118%20Bad%20Vilbel!5e1!3m2!1sde!2sde!4v1771572302025!5m2!1sde!2sde"
                            frameBorder="0"
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Location map"
                        />
                    </div>

                    <div className="row gy-4">

                        <div className="col-lg-4">
                            <div className="info-item d-flex" data-aos="fade-up" data-aos-delay="300">
                                <i className="bi bi-geo-alt flex-shrink-0"></i>
                                <div>
                                    <h3>Address</h3>
                                    <p>Lupinenweg 63, 61118 Bad Vilbel, Germany</p>
                                </div>
                            </div>

                            <div className="info-item d-flex" data-aos="fade-up" data-aos-delay="400">
                                <i className="bi bi-telephone flex-shrink-0"></i>
                                <div>
                                    <h3>Call me</h3>
                                    <p>+49 (0) 6101 99 54 846</p>
                                </div>
                            </div>

                            <div className="info-item d-flex" data-aos="fade-up" data-aos-delay="500">
                                <i className="bi bi-envelope flex-shrink-0"></i>
                                <div>
                                    <h3>Email me</h3>
                                    <p><img src={EMAILTEXT} alt="Email" /></p>
                                </div>
                            </div>

                        </div>

                        <div className="col-lg-8">
                            {recaptchaAllowed ? (
                                <form onSubmit={handleSubmit} className="php-email-form" data-aos="fade-up" data-aos-delay="200">
                                    <div className="row gy-4">

                                        <div className="col-md-6">
                                            <input type="text" name="FullName" className="form-control" placeholder="Your Name" required disabled={isSubmitting} />
                                        </div>

                                        <div className="col-md-6">
                                            <input type="email" className="form-control" name="email" placeholder="Your Email" required disabled={isSubmitting} />
                                        </div>

                                        <div className="col-md-12">
                                            <input type="text" className="form-control" name="phone" placeholder="Your Phone" disabled={isSubmitting} />
                                        </div>

                                        <div className="col-md-12">
                                            <textarea className="form-control" name="message" rows={6} placeholder="Message" required disabled={isSubmitting}></textarea>
                                        </div>

                                        <div className="col-md-12 text-center">
                                            <div className="loading" style={{ display: isSubmitting ? 'block' : 'none' }}>Loading</div>
                                            <div className="error-message" style={{ display: errorMessage ? 'block' : 'none' }}>{errorMessage}</div>
                                            <div className="sent-message" style={{ display: isSuccess ? 'block' : 'none' }}>Your message has been sent. Thank you!</div>

                                            <button type="submit" disabled={isSubmitting}>Send Message</button>
                                        </div>

                                    </div>
                                </form>
                            ) : (
                                <div
                                    className="d-flex flex-column align-items-start gap-3 p-4 rounded-3 border"
                                    style={{ background: 'var(--surface-color)', borderColor: 'rgba(70,83,103,0.2) !important' }}
                                    data-aos="fade-up"
                                    data-aos-delay="200"
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-shield-lock fs-4" style={{ color: 'var(--accent-color)' }}></i>
                                        <h5 className="mb-0" style={{ color: 'var(--heading-color)' }}>reCAPTCHA consent required</h5>
                                    </div>
                                    <p className="mb-0" style={{ color: 'var(--default-color)', lineHeight: '1.6' }}>
                                        This contact form is protected by Google reCAPTCHA to prevent spam and automated abuse.
                                        Please enable <strong>Google reCAPTCHA</strong> in your cookie settings to send a message.
                                    </p>
                                    <button
                                        className="btn btn-sm"
                                        style={{ background: 'var(--accent-color)', color: '#fff', fontFamily: 'var(--nav-font)' }}
                                        onClick={() => window.dispatchEvent(new CustomEvent(OPEN_CONSENT_EVENT))}
                                    >
                                        <i className="bi bi-sliders me-2"></i>Open Cookie Settings
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>

                </div>
            </section>
        </div>
    );
}

export default ContactMe;
